import { useCallback } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { useToast } from "@/hooks/useToast";
import { useAuthStore, useThreadStore } from "@/store";
import { useWorkspaceThreadsStore } from "@/store/workspaceThreadsStore";
import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import { SupabaseThreadUtils } from "@/lib/db/utils/supabase/threads";
import { refreshThreadsAndCleanup } from "@/lib/utils/threadCleanupUtils";
import { isPersonalProject } from "@/features/sidebar/projects/utils";
import { updateCachedWorkspaceThreads } from "@/features/sidebar/projects/hooks/useProjectThreads";
import { useChatMove } from "@/features/sidebar/projects/hooks/useChatMove";

/**
 * Extract real thread ID from namespaced sortable ID.
 * Sortable IDs are prefixed with "sidebar-" or "project-" to prevent
 * cross-interference between sidebar and project page during drag operations.
 */
const extractThreadId = (sortableId) => {
  if (typeof sortableId !== "string") return sortableId;
  if (sortableId.startsWith("sidebar-")) return sortableId.substring(8);
  if (sortableId.startsWith("project-")) return sortableId.substring(8);
  return sortableId;
};

/**
 * Centralized hook for all sidebar drag-and-drop operations.
 * Handles project reordering, chat reordering, and chat moving between workspaces.
 * This eliminates race conditions from multiple useDndMonitor listeners.
 */
export function useSidebarDndHandlers() {
  const { toast } = useToast();
  const { workspaces, setWorkspaces } = useAuthStore();
  // Use Zustand store for workspace threads (globally accessible)
  const { workspaceThreads, setWorkspaceThreads } = useWorkspaceThreadsStore();
  // Canonical atomic move (RPC + optimistic update + rollback + dedup + toast).
  const { handleChatMove: moveChatViaRpc } = useChatMove({
    workspaces,
    setWorkspaces,
    workspaceThreads,
    setWorkspaceThreads,
  });

  /**
   * Handle project reordering via drag and drop
   */
  const handleProjectReorder = useCallback(
    async (activeId, overId) => {
      if (activeId === overId) return;

      const activeIndex = workspaces.findIndex((w) => w.id === activeId);
      const overIndex = workspaces.findIndex((w) => w.id === overId);

      if (activeIndex === -1 || overIndex === -1) return;

      const activeWorkspace = workspaces[activeIndex];
      const overWorkspace = workspaces[overIndex];

      // Prevent reordering Personal Project
      if (
        isPersonalProject(activeWorkspace) ||
        isPersonalProject(overWorkspace)
      ) {
        return;
      }

      try {
        // Optimistically update local state
        const reorderedWorkspaces = arrayMove(
          workspaces,
          activeIndex,
          overIndex
        );

        // Update display_order values based on new positions
        // Personal Project stays at 0, others start at 1
        const updates = reorderedWorkspaces
          .filter((w) => !isPersonalProject(w))
          .map((workspace, index) => ({
            id: workspace.id,
            display_order: index + 1,
          }));

        // Update local state immediately
        const updatedWorkspaces = reorderedWorkspaces.map((w) => {
          if (isPersonalProject(w)) {
            return { ...w, display_order: 0 };
          }
          const update = updates.find((u) => u.id === w.id);
          return update ? { ...w, display_order: update.display_order } : w;
        });

        setWorkspaces(updatedWorkspaces);

        toast({
          title: "Success",
          description: "Projects reordered successfully",
        });

        // Update database in the background
        await SupabaseWorkspaceUtils.updateWorkspaceOrders(updates);
      } catch (error) {
        console.error("Error reordering projects:", error);
        setWorkspaces(workspaces);
        toast({
          title: "Error",
          description: "Failed to reorder projects. Please try again.",
          variant: "destructive",
        });
      }
    },
    [workspaces, setWorkspaces, toast]
  );

  /**
   * Handle chat reordering within a workspace
   */
  const handleChatReorder = useCallback(
    async (threads, activeId, overId, containerId) => {
      if (activeId === overId) return;

      // Extract real thread IDs from namespaced sortable IDs
      const realActiveId = extractThreadId(activeId);
      const realOverId = extractThreadId(overId);

      const activeIndex = threads.findIndex((t) => t.id === realActiveId);
      const overIndex = threads.findIndex((t) => t.id === realOverId);

      if (activeIndex === -1 || overIndex === -1) return;

      try {
        // Optimistically update local state
        const reorderedThreads = arrayMove(threads, activeIndex, overIndex);

        // Update display_order values on each thread to match new positions
        const reorderedWithUpdatedOrder = reorderedThreads.map(
          (thread, index) => ({
            ...thread,
            display_order: index,
          })
        );

        // Build updates array for database
        const updates = reorderedWithUpdatedOrder.map((thread) => ({
          id: thread.id,
          display_order: thread.display_order,
        }));

        // Update workspace threads state with updated display_order values
        if (setWorkspaceThreads && containerId) {
          setWorkspaceThreads((prev) => ({
            ...prev,
            [containerId]: reorderedWithUpdatedOrder,
          }));
          // Also update the module-level cache to prevent stale data on remount
          updateCachedWorkspaceThreads(containerId, reorderedWithUpdatedOrder);
        }

        // Update thread store optimistically
        useThreadStore.setState((state) => {
          const updatedThreads = state.threads.map((t) => {
            const update = updates.find((u) => u.id === t.id);
            if (update) {
              return { ...t, display_order: update.display_order };
            }
            return t;
          });
          return { threads: updatedThreads };
        });

        toast({
          title: "Success",
          description: "Chats reordered successfully",
        });

        // Dispatch custom event to notify components of reorder
        window.dispatchEvent(
          new CustomEvent("workspace-threads-reordered", {
            detail: {
              workspaceId: containerId,
              threads: reorderedWithUpdatedOrder,
            },
          })
        );

        // Update database in the background
        await SupabaseThreadUtils.updateThreadOrders(updates);
      } catch (error) {
        console.error("Error reordering chats:", error);
        toast({
          title: "Error",
          description: "Failed to reorder chats. Please try again.",
          variant: "destructive",
        });
      }
    },
    [setWorkspaceThreads, toast]
  );

  /**
   * Handle chat moving between workspaces (including from Chats to Project).
   * Delegates to useChatMove for the atomic RPC + optimistic update + rollback,
   * then fires the side-effects useChatMove does not perform.
   */
  const handleChatMove = useCallback(
    async (thread, targetWorkspaceId, sourceWorkspaceId = null) => {
      const currentWorkspaceId =
        sourceWorkspaceId ||
        workspaces.find((ws) => ws.workspace_threads?.includes(thread.id))
          ?.id ||
        null;

      // No-op if already in the target workspace
      if (currentWorkspaceId === targetWorkspaceId) {
        return;
      }

      // Atomic move via the canonical helper (RPC + optimistic update +
      // rollback + dedup + toast). Returns false on a no-op or failed move,
      // in which case we skip the side-effects below.
      const moved = await moveChatViaRpc(
        thread,
        targetWorkspaceId,
        sourceWorkspaceId
      );
      if (!moved) return;

      // Side-effects useChatMove does NOT perform, preserved from the
      // original inline implementation:
      await refreshThreadsAndCleanup();
      window.dispatchEvent(
        new CustomEvent("workspace-thread-moved", {
          detail: {
            threadId: thread.id,
            targetWorkspaceId,
            sourceWorkspaceId: currentWorkspaceId,
          },
        })
      );
    },
    [moveChatViaRpc, workspaces]
  );

  /**
   * Main drag end handler that dispatches to appropriate sub-handler
   */
  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      if (!over) return;

      const activeData = active.data.current;
      const overData = over.data.current;

      if (!activeData) return;

      // 1. Project reordering
      if (activeData.type === "project" && overData?.type === "project") {
        handleProjectReorder(active.id, over.id);
        return;
      }

      // 2. Chat reordering within the same workspace AND same source (sidebar or project)
      // Ensures sidebar reordering doesn't affect project page and vice versa
      if (
        activeData.type === "chat" &&
        overData?.type === "chat" &&
        activeData.workspaceId === overData.workspaceId &&
        activeData.workspaceId !== null &&
        activeData.source === overData.source // Must be same source (sidebar-sidebar or project-project)
      ) {
        const containerId = activeData.workspaceId;
        const threads = workspaceThreads?.[containerId] || [];
        handleChatReorder(threads, active.id, over.id, containerId);
        return;
      }

      // 3. Chat moving between workspaces (including Chats → Project and Project → Chats)
      // Only allow moves from sidebar - cross-area drag (project page to sidebar) is not supported
      if (activeData.type === "chat" && activeData.source === "sidebar") {
        let targetWorkspaceId = null;
        const sourceWorkspaceId = activeData.workspaceId || null;

        // Determine target workspace from various drop scenarios
        if (overData?.type === "chats") {
          // Dropping on Chats section - move to Chats (null workspace)
          targetWorkspaceId = null;
        } else if (overData?.type === "workspace" && overData.workspaceId) {
          targetWorkspaceId = overData.workspaceId;
        } else if (overData?.type === "project" && overData.item?.id) {
          targetWorkspaceId = overData.item.id;
        } else if (overData?.type === "chat" && overData.workspaceId) {
          targetWorkspaceId = overData.workspaceId;
        } else if (over.id) {
          // Fallback: check if over.id is a workspace ID
          const matchingWorkspace = workspaces.find((w) => w.id === over.id);
          if (matchingWorkspace) {
            targetWorkspaceId = matchingWorkspace.id;
          } else if (
            typeof over.id === "string" &&
            over.id.startsWith("workspace-drop-")
          ) {
            const workspaceIdFromId = over.id.replace("workspace-drop-", "");
            if (workspaces.some((w) => w.id === workspaceIdFromId)) {
              targetWorkspaceId = workspaceIdFromId;
            }
          }
        }

        // Move the thread if target is different from source
        // For Chats target (null), only move if source is a project (not null)
        if (targetWorkspaceId !== sourceWorkspaceId) {
          // For moving to Chats, source must be a project
          if (targetWorkspaceId === null && sourceWorkspaceId === null) {
            return; // Already in Chats, do nothing
          }
          const thread = activeData.item;
          handleChatMove(thread, targetWorkspaceId, sourceWorkspaceId);
          return;
        }
      }
    },
    [
      handleProjectReorder,
      handleChatReorder,
      handleChatMove,
      workspaceThreads,
      workspaces,
    ]
  );

  return {
    handleDragEnd,
    handleProjectReorder,
    handleChatReorder,
    handleChatMove,
  };
}
